/**
 * Ticket Lifecycle v2 — Slice 4C.1
 *
 * Pure helper for deriving the FOH Queue nav-button visual state from
 * the current custody counts. Used by `TopBar` to render an at-a-glance
 * status pill on the FOH Queue button (urgent / aging / returned /
 * normal).
 *
 * Tone priority (most-urgent wins; FOH attention is the primary signal):
 *   1. pending_advisor_accept > 0      → 'urgent'   (red, pulsing)
 *   2. with_advisor > 0                → 'aging'    (amber)
 *   3. pending_tech_accept only        → 'returned' (secondary; the tech
 *                                                    is the actor for
 *                                                    these, not FOH)
 *   4. total == 0                      → 'normal'   (no badge / no loud
 *                                                    color)
 *
 * Pure: no clock reads, no I/O. Consumers pass the counts they fetched.
 */

export interface FohQueueCounts {
  pendingAdvisorAccept: number;
  withAdvisor: number;
  pendingTechAccept: number;
  /**
   * FOH Workboard: the Needs Attention count (the board's own lane rule).
   * When present it is the badge number; pending_tech_accept is the
   * technician's move and only tints the button ('returned').
   */
  needsAttention?: number;
}

export type FohNavTone = 'normal' | 'returned' | 'aging' | 'urgent';

export interface FohNavStatus {
  tone: FohNavTone;
  total: number;
}

export function totalFohQueueCount(counts: FohQueueCounts): number {
  return (
    counts.pendingAdvisorAccept +
    counts.withAdvisor +
    counts.pendingTechAccept
  );
}

export function getFohNavStatus(counts: FohQueueCounts): FohNavStatus {
  const total = counts.needsAttention ?? totalFohQueueCount(counts);
  if (counts.pendingAdvisorAccept > 0) return { tone: 'urgent', total };
  if (counts.withAdvisor > 0) return { tone: 'aging', total };
  if (counts.pendingTechAccept > 0) return { tone: 'returned', total };
  return { tone: 'normal', total };
}
